// src/pages/Connector/Salesforce/CreateEditLeadModal.js
import React, { useState, useEffect } from "react";
import { Modal as MuiModal, Box, Button as MuiButton, TextField, MenuItem, Alert } from '@mui/material';
import axiosInstance from "../../../utils/axiosInstance";

export default function CreateEditLeadModal({ isOpen, onClose, onSuccess, lead, mode, permission }) {
    const [formData, setFormData] = useState({
        FirstName: '',
        LastName: '',
        Company: '',
        Email: '',
        Phone: '',
        Status: 'Open - Not Contacted',
        Title: '',
        Street: '',
        City: '',
        State: '',
        PostalCode: '',
        Country: '',
        Website: '',
        Description: ''
    });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');

    const statusOptions = [
        'Open - Not Contacted',
        'Working - Contacted',
        'Closed - Converted',
        'Closed - Not Converted'
    ];

    useEffect(() => {
        if (lead && mode === 'edit') {
            setFormData({
                FirstName: lead.FirstName || '',
                LastName: lead.LastName || '',
                Company: lead.Company || '',
                Email: lead.Email || '',
                Phone: lead.Phone || '',
                Status: lead.Status || 'Open - Not Contacted',
                Title: lead.Title || '',
                Street: lead.Street || '',
                City: lead.City || '',
                State: lead.State || '',
                PostalCode: lead.PostalCode || '',
                Country: lead.Country || '',
                Website: lead.Website || '',
                Description: lead.Description || ''
            });
        }
    }, [lead, mode]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error for this field
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.LastName.trim()) {
            newErrors.LastName = 'Last Name is required';
        }
        
        if (!formData.Company.trim()) {
            newErrors.Company = 'Company is required';
        }
        
        if (formData.Email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.Email)) {
            newErrors.Email = 'Invalid email format';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);
        setSubmitError('');

        try {
            // Remove empty fields
            const cleanedData = Object.entries(formData).reduce((acc, [key, value]) => {
                if (value && value.toString().trim() !== '') {
                    acc[key] = value;
                }
                return acc;
            }, {});

            if (mode === 'create') {
                await axiosInstance.post('/api/salesforce/leads', cleanedData);
            } else {
                await axiosInstance.put(`/api/salesforce/leads/${lead.Id}`, cleanedData);
            }
            
            onSuccess();
        } catch (err) {
            console.error(`Error ${mode === 'create' ? 'creating' : 'updating'} lead:`, err);
            setSubmitError(err.response?.data?.message || `Failed to ${mode === 'create' ? 'create' : 'update'} lead`);
        } finally {
            setIsSubmitting(false);
        }
    };

    const modalStyle = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: { xs: '95%', sm: '90%', md: '70%' },
        maxWidth: '800px',
        maxHeight: '90vh',
        bgcolor: 'background.paper',
        boxShadow: 24,
        p: 3,
        borderRadius: '8px',
        outline: 'none',
        overflowY: 'auto'
    };

    // Check if user can modify (for edit mode) or create (for create mode)
    const canPerformAction = mode === 'create' ? permission.canModify : permission.canModify;

    return (
        <MuiModal open={isOpen} onClose={onClose} aria-labelledby="lead-modal-title">
            <Box sx={modalStyle}>
                <h5 id="lead-modal-title" style={{ marginTop: 0, marginBottom: '20px' }}>
                    {mode === 'create' ? 'Create New Lead' : 'Edit Lead'}
                </h5>

                {!canPerformAction && (
                    <Alert severity="warning" sx={{ mb: 3 }}>
                        You don't have permission to {mode === 'create' ? 'create' : 'edit'} leads. Contact your administrator.
                    </Alert>
                )}

                {submitError && (
                    <div className="alert alert-danger mb-3" role="alert">
                        {submitError}
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <TextField
                                fullWidth
                                label="First Name"
                                name="FirstName"
                                value={formData.FirstName}
                                onChange={handleChange}
                                variant="outlined"
                                disabled={!canPerformAction}
                            />
                        </div>
                        <div className="col-md-6 mb-3">
                            <TextField
                                fullWidth
                                required
                                label="Last Name"
                                name="LastName"
                                value={formData.LastName}
                                onChange={handleChange}
                                error={!!errors.LastName}
                                helperText={errors.LastName}
                                variant="outlined"
                                disabled={!canPerformAction}
                            />
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <TextField
                                fullWidth
                                required
                                label="Company"
                                name="Company"
                                value={formData.Company}
                                onChange={handleChange}
                                error={!!errors.Company}
                                helperText={errors.Company}
                                variant="outlined"
                                disabled={!canPerformAction}
                            />
                        </div>
                        <div className="col-md-6 mb-3">
                            <TextField
                                fullWidth
                                label="Title"
                                name="Title"
                                value={formData.Title}
                                onChange={handleChange}
                                variant="outlined"
                                disabled={!canPerformAction}
                            />
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <TextField
                                fullWidth
                                label="Email"
                                name="Email"
                                type="email"
                                value={formData.Email}
                                onChange={handleChange}
                                error={!!errors.Email}
                                helperText={errors.Email}
                                variant="outlined"
                                disabled={!canPerformAction}
                            />
                        </div>
                        <div className="col-md-6 mb-3">
                            <TextField
                                fullWidth
                                label="Phone"
                                name="Phone"
                                value={formData.Phone}
                                onChange={handleChange}
                                variant="outlined"
                                disabled={!canPerformAction}
                            />
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <TextField
                                fullWidth
                                select
                                label="Status"
                                name="Status"
                                value={formData.Status}
                                onChange={handleChange}
                                variant="outlined"
                                disabled={!canPerformAction}
                            >
                                {statusOptions.map((option) => (
                                    <MenuItem key={option} value={option}>
                                        {option}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </div>
                        <div className="col-md-6 mb-3">
                            <TextField
                                fullWidth
                                label="Website"
                                name="Website"
                                value={formData.Website}
                                onChange={handleChange}
                                variant="outlined"
                                disabled={!canPerformAction}
                            />
                        </div>
                    </div>

                    <h6 className="mt-3 mb-2">Address Information</h6>
                    <div className="row">
                        <div className="col-12 mb-3">
                            <TextField
                                fullWidth
                                label="Street"
                                name="Street"
                                value={formData.Street}
                                onChange={handleChange}
                                variant="outlined"
                                disabled={!canPerformAction}
                            />
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <TextField
                                fullWidth
                                label="City"
                                name="City"
                                value={formData.City}
                                onChange={handleChange}
                                variant="outlined"
                                disabled={!canPerformAction}
                            />
                        </div>
                        <div className="col-md-6 mb-3">
                            <TextField
                                fullWidth
                                label="State"
                                name="State"
                                value={formData.State}
                                onChange={handleChange}
                                variant="outlined"
                                disabled={!canPerformAction}
                            />
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <TextField
                                fullWidth
                                label="Postal Code"
                                name="PostalCode"
                                value={formData.PostalCode}
                                onChange={handleChange}
                                variant="outlined"
                                disabled={!canPerformAction}
                            />
                        </div>
                        <div className="col-md-6 mb-3">
                            <TextField
                                fullWidth
                                label="Country"
                                name="Country"
                                value={formData.Country}
                                onChange={handleChange}
                                variant="outlined"
                                disabled={!canPerformAction}
                            />
                        </div>
                    </div>

                    <div className="mb-3">
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            label="Description"
                            name="Description"
                            value={formData.Description}
                            onChange={handleChange}
                            variant="outlined"
                            disabled={!canPerformAction}
                        />
                    </div>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
                        <MuiButton variant="outlined" onClick={onClose} disabled={isSubmitting}>
                            Cancel
                        </MuiButton>
                        {canPerformAction && (
                            <MuiButton 
                                type="submit"
                                variant="contained" 
                                disabled={isSubmitting}
                                startIcon={isSubmitting && <i className="fa fa-spinner fa-spin"/>}
                            >
                                {isSubmitting ? (mode === 'create' ? 'Creating...' : 'Updating...') : (mode === 'create' ? 'Create Lead' : 'Update Lead')}
                            </MuiButton>
                        )}
                    </Box>
                </form>
            </Box>
        </MuiModal>
    );
}